import { ReactElement, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, ButtonGroup, Container, IconButton, InputAdornment, Paper, Stack, TextField, useTheme } from "@mui/material";
import PageHeader from "@/components/PageHeader";
import { setContainer } from "@/store/uiSlice";
import { useAppDispatch, useAppSelector } from "@/hooks";
import IncomingOrders from "./IncomingOrders";
import useAccessControl from "@/features/access-control/useAccessControl";
import CreateOrderDrawer from "./components/CreateOrderDrawer";
import { Cancel01Icon, Layout3ColumnIcon, Layout3RowIcon, Search01Icon } from "@hugeicons/core-free-icons";
import FakeOrdersButton from "./components/FakeOrdersButton";
import OrdersGrid from "./components/OrdersGrid/OrdersGrid";
import { OrdersPageProvider, useOrdersPageContext } from "./OrdersPageContext";
import { HugeiconsIcon } from "@hugeicons/react";
import { Branch } from "@/types/branch";
import TutorialsSubscriber from "@/components/TutorialsSubscriber";
import { ProductIds } from "@/constants/productIds";

const INITIAL_TAB = "incoming";

export enum ActiveView {
  List = "list",
  Stages = "stages",
}

export const views = [
  {
    id: ActiveView.Stages,
    icon: Layout3ColumnIcon,
    label: "Columnas",
  },
  {
    id: ActiveView.List,
    icon: Layout3RowIcon,
    label: "Lista",
  },
];

const tabs: Record<string, Tab> = {
  incoming: {
    label: "Entrantes",
    component: <IncomingOrders />,
  },
  orders: {
    label: "Historial",
    component: <OrdersGrid />,
  },
};

interface Tab {
  label: string;
  component: ReactElement;
}

type Tabs = keyof typeof tabs;

const OrdersPageContent = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const container = useAppSelector((state) => state.ui?.container);
  const { hasAccess } = useAccessControl();
  const { organizationId } = useParams<{ organizationId: string }>();
  const organization = useAppSelector((state) => state.organizations[organizationId]);
  const activeBranch = useAppSelector((state) => state.ui.activeBranch);
  const branchName = organization?.branches?.find((branch: Branch) => branch.id === activeBranch)?.title;
  const [createOrderDrawerOpen, setCreateOrderDrawerOpen] = useState(false);
  const { name, setName, lastname, setLastname, orderNumber, setOrderNumber } = useOrdersPageContext();

  const activeTab = useMemo(() => {
    const qs = new URLSearchParams(window.location.search);
    return qs.get("tab") || INITIAL_TAB;
  }, [window.location.search]);

  const handleChangeTab = (newValue: Tabs) => {
    const qs = new URLSearchParams(window.location.search);
    qs.set("tab", newValue);
    navigate(`?${qs.toString()}`);
  };

  useEffect(() => {
    dispatch(setContainer("fluid"));
    return () => {
      dispatch(setContainer("fixed"));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack height={"100%"} spacing={2}>
      <TutorialsSubscriber productIds={[ProductIds.ORDERS_GRID]} />
      <PageHeader
        title={activeBranch ? `Pedidos - ${branchName}` : "Pedidos"}
        action={
          <Stack direction="row" spacing={1}>
            <FakeOrdersButton />
            {hasAccess("create.order") && (
              <Button color="success" onClick={() => setCreateOrderDrawerOpen(true)}>
                Crear Orden
              </Button>
            )}
          </Stack>
        }
      />
      <Container
        maxWidth={container === 'fixed' ? 'lg' : false}
        sx={{
          height: '100%',
        }}
      >
        <Stack spacing={2} height={'100%'}>
          <Paper>
            <Stack p={2} direction={{ sm: 'row' }} gap={2} justifyContent="space-between" alignItems="center">
              {activeTab === "orders" && (
                <Stack direction={{ sm: 'row' }} gap={2} flex={1} width="100%" >
                  <TextField
                    placeholder="# Orden"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    variant="outlined"
                    size="small"
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HugeiconsIcon icon={Search01Icon} size={20} color={theme.palette.primary.main} />
                        </InputAdornment>
                      ),
                      endAdornment: orderNumber ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setOrderNumber("")}>
                            <HugeiconsIcon icon={Cancel01Icon} size={20} color={theme.palette.error.main} />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }}
                  />
                  <TextField
                    placeholder="Nombre"
                    value={name}
                    onChange={(e) => setName(e.target.value.replace(/[^a-zA-Z\s]/g, ""))}
                    variant="outlined"
                    size="small"
                    type="text"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HugeiconsIcon icon={Search01Icon} size={20} color={theme.palette.primary.main} />
                        </InputAdornment>
                      ),
                      endAdornment: name ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setName("")}>
                            <HugeiconsIcon icon={Cancel01Icon} size={20} color={theme.palette.error.main} />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }}
                  />
                  <TextField
                    placeholder="Apellido"
                    value={lastname}
                    onChange={(e) => setLastname(e.target.value.replace(/[^a-zA-Z\s]/g, ""))}
                    variant="outlined"
                    size="small"
                    type="text"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HugeiconsIcon icon={Search01Icon} size={20} color={theme.palette.primary.main} />
                        </InputAdornment>
                      ),
                      endAdornment: lastname ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setLastname("")}>
                            <HugeiconsIcon icon={Cancel01Icon} size={20} color={theme.palette.error.main} />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }}
                  />
                </Stack>
              )}
              <ButtonGroup variant="outlined" aria-label="Basic button group">
                {Object.entries(tabs).map(([tabKey, tab]) => (
                  <Button key={tabKey} variant={activeTab === tabKey ? "contained" : "outlined"} onClick={() => handleChangeTab(tabKey)}>
                    {tab.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Stack>
          </Paper>
          <Stack spacing={2} flex={1}>
            {tabs[activeTab]?.component}
          </Stack>
          <CreateOrderDrawer open={createOrderDrawerOpen} onClose={() => setCreateOrderDrawerOpen(false)} />
        </Stack>
      </Container>
    </Stack>
  );
};

const OrdersPage = () => (
  <OrdersPageProvider>
    <OrdersPageContent />
  </OrdersPageProvider>
);

export default OrdersPage;
